import { useStore } from "@/lib/store";
import { billingApi, isMockMode } from "@/lib/api";
import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { initializePaddle, type Paddle } from "@paddle/paddle-js";
import {
  Check,
  X,
  AlertTriangle,
  ExternalLink,
  Loader2,
  Sparkles,
} from "lucide-react";

const PRICE = "$9.99/mo";

const features = [
  { label: "Overview Analytics", free: true, pro: true },
  { label: "Follower Tracking", free: true, pro: true },
  { label: "7-day Data", free: true, pro: true },
  { label: "Post-level Analytics", free: false, pro: true },
  { label: "Engagement Heatmap", free: false, pro: true },
  { label: "30 & 90-day History", free: false, pro: true },
  { label: "Multi-account (up to 5)", free: false, pro: true },
  { label: "Priority Support", free: false, pro: true },
];

export function BillingPage() {
  const { user, setUser, isPro, notify } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const paddleRef = useRef<Paddle | null>(null);
  const queryClient = useQueryClient();

  const isProUser = isPro();
  const status = user?.subscriptionStatus || "free";
  const showSuccess =
    searchParams.get("success") === "true" || checkoutSuccess;
  const showCanceled = searchParams.get("canceled") === "true";

  // Auto-clear banners after 5s
  useEffect(() => {
    if (showSuccess || showCanceled) {
      const timer = setTimeout(() => {
        setSearchParams({}, { replace: true });
        setCheckoutSuccess(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess, showCanceled, setSearchParams]);

  // Handle Paddle checkout events
  const handlePaddleEvent = useCallback(
    (event: { name?: string }) => {
      if (event.name === "checkout.completed") {
        setCheckoutSuccess(true);
        // Refresh user data to pick up new subscription status (after webhook fires)
        queryClient.invalidateQueries({ queryKey: ["user"] });
      }
    },
    [queryClient]
  );

  if (!user) return null;

  async function handleCheckout() {
    if (isMockMode) {
      const currentUser = user;
      if (!currentUser) return;
      setCheckoutSuccess(true);
      setLoading(false);
      const updatedUser = { ...currentUser, subscriptionStatus: "pro" };
      setUser(updatedUser);
      queryClient.setQueryData(["user"], updatedUser);
      return;
    }

    setLoading(true);
    try {
      const checkoutData = await billingApi.checkout();

      // Initialize Paddle.js if not already done
      if (!paddleRef.current) {
        const paddle = await initializePaddle({
          token: checkoutData.paddleClientToken,
          environment:
            checkoutData.paddleEnvironment === "production"
              ? "production"
              : "sandbox",
          eventCallback: handlePaddleEvent,
        });
        paddleRef.current = paddle || null;
      }

      if (!paddleRef.current) {
        throw new Error("Failed to initialize Paddle");
      }

      // Open Paddle overlay checkout — runs in-app, no redirect!
      paddleRef.current.Checkout.open({
        items: [{ priceId: checkoutData.paddlePriceId, quantity: 1 }],
        customData: { userId: checkoutData.userId },
        settings: {
          displayMode: "overlay",
          theme: "light",
          successUrl: `${window.location.origin}/dashboard/billing?success=true`,
        },
      });
    } catch (err: any) {
      notify({
        type: "error",
        message: err.message || "Failed to start checkout",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handlePortal() {
    setLoading(true);
    try {
      const { url } = await billingApi.portal();
      window.location.href = url;
    } catch (err: any) {
      notify({
        type: "error",
        message: err.message || "Failed to open billing portal",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight mb-6">Billing</h1>

      {/* Success banner */}
      {showSuccess && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 p-4">
          <Sparkles className="h-5 w-5 text-green-600 shrink-0" />
          <div>
            <p className="font-semibold text-green-900">Welcome to Pro!</p>
            <p className="text-sm text-green-700">
              Your subscription is active. Enjoy post-level analytics, heatmaps,
              and more.
            </p>
          </div>
        </div>
      )}

      {/* Canceled banner */}
      {showCanceled && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-border bg-surface p-4">
          <X className="h-5 w-5 text-neutral-400 shrink-0" />
          <p className="text-sm text-neutral-600">
            Checkout was canceled. You can upgrade anytime.
          </p>
        </div>
      )}

      {/* Past due warning */}
      {status === "past_due" && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div>
            <p className="font-semibold text-amber-900">Payment failed</p>
            <p className="text-sm text-amber-700">
              Please update your payment method to keep Pro features active.
            </p>
          </div>
        </div>
      )}

      {/* Current plan */}
      <section className="rounded-xl border border-border bg-white p-5 mb-6">
        <h2 className="text-sm font-medium uppercase tracking-wide text-neutral-400 mb-4">
          Current Plan
        </h2>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wide ${
                isProUser
                  ? "bg-black text-white"
                  : status === "past_due"
                    ? "bg-amber-100 text-amber-700"
                    : status === "canceled"
                      ? "bg-red-50 text-red-600"
                      : "bg-neutral-100 text-neutral-500"
              }`}
            >
              {status === "past_due" ? "Past Due" : status}
            </span>
            {isProUser && (
              <span className="text-sm text-neutral-500">{PRICE}</span>
            )}
          </div>

          {/* Action button */}
          {isProUser || status === "past_due" ? (
            <button
              onClick={handlePortal}
              disabled={loading}
              className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-surface-hover transition-colors disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4" />
              )}
              Manage Subscription
            </button>
          ) : status === "canceled" ? (
            <button
              onClick={handleCheckout}
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 transition-colors disabled:opacity-50"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Re-subscribe to Pro
            </button>
          ) : (
            <button
              onClick={handleCheckout}
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 transition-colors disabled:opacity-50"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Upgrade to Pro — {PRICE}
            </button>
          )}
        </div>
      </section>

      {/* Feature comparison */}
      <section className="rounded-xl border border-border bg-white p-5">
        <h2 className="text-sm font-medium uppercase tracking-wide text-neutral-400 mb-4">
          Plan Comparison
        </h2>

        {/* Header */}
        <div className="grid grid-cols-3 gap-4 border-b border-border pb-3 mb-3">
          <div />
          <div className="text-center text-sm font-medium text-neutral-500">
            Free
          </div>
          <div className="text-center text-sm font-medium">Pro — {PRICE}</div>
        </div>

        {/* Feature rows */}
        <div className="space-y-0">
          {features.map((f) => (
            <div
              key={f.label}
              className="grid grid-cols-3 gap-4 items-center py-2.5 border-b border-border last:border-b-0"
            >
              <span className="text-sm text-neutral-700">{f.label}</span>
              <div className="flex justify-center">
                {f.free ? (
                  <Check className="h-4 w-4 text-black" />
                ) : (
                  <X className="h-4 w-4 text-neutral-300" />
                )}
              </div>
              <div className="flex justify-center">
                <Check className="h-4 w-4 text-black" />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
